import React, { useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { AccordionSection } from 'components/Accordions';
import { CardIcon } from 'components/Card';
import { Empty } from 'components/Empty';
import { Icon, IconTypes } from 'components/Icon';
import { Tag } from 'components/Tag';
import { Tooltip } from 'components/Tooltip';
import { H4, Text } from 'components/Typography';
import { Appearance, theme, toRem } from 'design';
import { URL } from 'routes';
import { setCurrentSite } from 'store/App';
import { ISite } from 'types/Site';
import { centsToDollarsDisplay } from 'utils/currency';

const Layout = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  list-style: none;

  .site-item-header {
    background-color: ${theme.color.neutral[100]};
  }
`;

const AccordionSubtitle = styled.div`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;

  .address-tooltip-icon {
    fill: ${theme.color.neutral[200]};

    &:hover,
    &:focus {
      fill: ${theme.color.neutral[400]};
    }
  }

  /* Hide address text on mobile */
  .address-text {
    display: none;

    @media (min-width: ${theme.breakpoint.med}px) {
      display: block;
    }
  }

  /* Hide tooltip content on desktop */
  @media (min-width: ${theme.breakpoint.med}px) {
    .address-tooltip-icon,
    .address-tooltip-content {
      display: none;
    }
  }
`;

const TagTooltip = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: ${toRem(theme.space.xs)};

  > * {
    color: ${theme.color.white};
  }
`;

const InnerContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(theme.space.md)};
  width: 100%;
  padding: ${toRem(theme.space.md)};

  @media (min-width: 870px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ItemLayout = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${toRem(55)};
  padding: 0 ${toRem(theme.space.md)};

  .overview-item-icon {
    width: ${toRem(33)};
    height: ${toRem(33)};
    margin-right: ${toRem(theme.space.md)};

    svg {
      width: ${toRem(16.5)};
      height: ${toRem(16.5)};
    }
  }
`;

const ItemDetails = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  width: 100%;
`;

const ActionButton = styled.button`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
  color: ${theme.color.neutral[800]};
  width: 100%;
`;

interface ISiteOverviewItemProps {
  actionLabel: string;
  icon?: IconTypes;
  iconBGColor?: string;
  iconFill?: string;
  isLoading?: boolean;
  onClick: () => void;
  title: string;
  value: string;
  tooltip?: string;
}
const SiteOverviewItem: React.FC<ISiteOverviewItemProps> = ({ title, value, actionLabel, onClick, icon, iconFill, iconBGColor, isLoading }) => (
  <ItemLayout data-cy="site-overview-item">
    <CardIcon className="overview-item-icon" icon={icon} fill={iconFill} bgColor={iconBGColor} isLoading={isLoading} data-cy="overview-item-icon" />
    <ItemDetails data-cy="overview-item-details">
      <Text size="xxsmall" fontWeight="semibold">
        {isLoading ? <Skeleton width={75} /> : title}
      </Text>
      <Text size="small">{isLoading ? <Skeleton width={50} /> : value}</Text>
    </ItemDetails>

    <ActionButton onClick={onClick} data-cy="overview-item-action-button">
      <Text size="xxsmall" fontWeight="medium">
        {isLoading ? <Skeleton width={75} /> : actionLabel}
      </Text>
      {!isLoading && <Icon icon="Next" width={7} height={11} />}
    </ActionButton>
  </ItemLayout>
);

interface IAdminSitesListProps {
  dataCy?: string;
  isLoading?: boolean;
  sites: ISite[];
  limit?: number;
}

const AdminSitesList: React.FC<IAdminSitesListProps> = ({ sites, isLoading, dataCy = 'admin-sites-list', limit = 5 }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const navigateToSitePage = useCallback(
    (siteId: string, page: URL) => {
      dispatch(setCurrentSite(siteId));
      history.push(page);
    },
    [history, dispatch]
  );

  if (isLoading) {
    return (
      <Layout data-cy={`${dataCy}-loading`}>
        {[...Array(limit)].map((_, index) => (
          <AccordionSection
            key={index}
            className="site-item"
            compact
            as="li"
            hideContentOverflow
            isLoading
            title={<Skeleton width={100} height={20} />}
            subtitle={<Skeleton width={100} height={20} />}
            actions={<Skeleton width={100} height={20} />}
          >
            <InnerContent>
              {[...Array(6)].map((_, i) => (
                <SiteOverviewItem key={i} isLoading title="" value="" actionLabel="" iconBGColor="" iconFill="" onClick={() => {}} />
              ))}
            </InnerContent>
          </AccordionSection>
        ))}
      </Layout>
    );
  }

  return (
    <Layout data-cy={dataCy}>
      {sites?.length === 0 && !isLoading ? (
        <Empty container containerHeight={300} icon="No" message="No sites found. Try another search." />
      ) : (
        sites?.map((site) => {
          const getTooltipId = `tooltip-${site.name}`;
          const formattedAddress = site.address ? `${site.address.address1}, ${site.address.city}, ${site.address.state}, ${site.address.zipCode}` : '';

          return (
            <AccordionSection
              key={`admin-site-${site.id}`}
              className="site-item"
              dataCy={`${dataCy}-item`}
              compact
              as="li"
              hideContentOverflow
              isLoading={isLoading}
              title={`${site.name}${site.customerSiteId ? ` (${site.customerSiteId})` : ''}`}
              subtitle={
                <AccordionSubtitle>
                  <H4 className="address-text">{formattedAddress}</H4>
                  <Icon className="address-tooltip-icon" icon="Site" width={13.75} label="Hover for address." tooltipId={getTooltipId} />
                  <Tooltip className="address-tooltip-content" id={getTooltipId} place="right">
                    {formattedAddress}
                  </Tooltip>
                </AccordionSubtitle>
              }
              actions={
                site.stats.balance.pendingFunds > 0 ? (
                  <Tag
                    label="Pending Recharge"
                    appearance={Appearance.WARNING}
                    tooltipId={`pending-recharge-${site.id}`}
                    tooltipMessage={
                      <TagTooltip>
                        <Text size="xxsmall">Amount: {centsToDollarsDisplay(site.stats.balance.pendingFunds)}</Text>
                      </TagTooltip>
                    }
                    tooltipPlace="left"
                  />
                ) : null
              }
            >
              <InnerContent>
                <SiteOverviewItem
                  title="Account Balance"
                  icon="Wallet"
                  iconFill={theme.color.neutral[400]}
                  iconBGColor={theme.color.neutral[100]}
                  value={centsToDollarsDisplay(site.stats.balance.current || 0)}
                  actionLabel="Review Payments"
                  onClick={() => navigateToSitePage(site.id, URL.PaymentsOverview)}
                  isLoading={isLoading}
                />
                <SiteOverviewItem
                  title="Retainer"
                  icon="Wallet"
                  iconFill={theme.color.neutral[400]}
                  iconBGColor={theme.color.neutral[100]}
                  value={centsToDollarsDisplay(site.settings.retainer || 0)}
                  actionLabel="Review Payments"
                  onClick={() => navigateToSitePage(site.id, URL.PaymentsOverview)}
                  isLoading={isLoading}
                />
                <SiteOverviewItem
                  title="7 Day Total"
                  icon="Payment"
                  iconFill={theme.color.success[50]}
                  iconBGColor={theme.color.success[25]}
                  value={centsToDollarsDisplay(site.stats.payouts.total.net || 0)}
                  actionLabel="Review Payments"
                  onClick={() => navigateToSitePage(site.id, URL.PaymentsOverview)}
                  isLoading={isLoading}
                />
                <SiteOverviewItem
                  title="Expired Payments"
                  icon="Payment"
                  iconFill={theme.color.danger[50]}
                  iconBGColor={theme.color.danger[25]}
                  value={site.stats.payouts.expired.toString() || '0'}
                  actionLabel="View Payments"
                  onClick={() => navigateToSitePage(site.id, URL.PaymentsDetail)}
                  isLoading={isLoading}
                />
                <SiteOverviewItem
                  title="Total Users"
                  icon="People"
                  iconFill={theme.color.neutral[400]}
                  iconBGColor={theme.color.neutral[100]}
                  value={site.stats.employees.approved.toString() || '0'}
                  actionLabel="Manage Users"
                  onClick={() => navigateToSitePage(site.id, URL.Employees)}
                  isLoading={isLoading}
                />
                <SiteOverviewItem
                  title="Users Requiring Approval"
                  icon="Person"
                  iconFill={theme.color.primary[500]}
                  iconBGColor={theme.color.primary[25]}
                  value={site.stats.employees.actionRequired.toString() || '0'}
                  actionLabel="Review Users"
                  onClick={() => navigateToSitePage(site.id, URL.EmployeesNeedingApproval)}
                  isLoading={isLoading}
                />
              </InnerContent>
            </AccordionSection>
          );
        })
      )}
    </Layout>
  );
};

export default AdminSitesList;
